import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Wallet, AlertTriangle, Clock, ArrowRight } from "lucide-react";

const REC_STYLES = {
  pay_now_overdue: { badge: "bg-red-100 text-red-700", border: "border-red-300", bg: "bg-red-50", label: "Sofort zahlen — überfällig" },
  pay_now_skonto: { badge: "bg-emerald-100 text-emerald-700", border: "border-emerald-300", bg: "bg-emerald-50", label: "Jetzt zahlen — Skonto sichern" },
  pay_due_soon: { badge: "bg-amber-100 text-amber-700", border: "border-amber-200", bg: "bg-amber-50", label: "Bald zahlen" },
  pay_later: { badge: "bg-neutral-100 text-neutral-700", border: "border-neutral-200", bg: "bg-white", label: "Kann warten" },
};

export default function PaymentPrioritiesPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/payment-priorities").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-neutral-500" data-testid="pp-loading">Lade…</div>;
  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="payment-priorities-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Cashflow-Steuerung</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-3">
          <Wallet className="h-7 w-7" /> Zahlungs­prioritäten
        </h1>
        <p className="text-sm text-neutral-500 mt-2">
          AgencyCash sortiert deine offenen Eingangsrechnungen nach Dringlichkeit. Skonto-Fenster, Überfälligkeit und Fälligkeit werden automatisch berücksichtigt.
        </p>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <Stat label="Offen gesamt" value={fmtCurrency(s.total_open_amount)} testid="pp-stat-open" />
        <Stat label="Überfällig" value={fmtCurrency(s.total_overdue_amount)} accent="red" testid="pp-stat-overdue" />
        <Stat label="Skonto verfügbar" value={fmtCurrency(s.total_skonto_savings_available)} accent="green" testid="pp-stat-skonto" />
        <Stat label="Rechnungen" value={s.count_total} testid="pp-stat-count" />
      </div>

      {s.total_skonto_savings_available > 0 && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 flex items-start gap-3" data-testid="pp-skonto-banner">
          <Clock className="h-5 w-5 text-emerald-700 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <strong>{fmtCurrency(s.total_skonto_savings_available)}</strong> kannst du sparen, wenn du die {s.count_skonto} Rechnung(en) mit aktivem Skonto rechtzeitig zahlst.
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="pp-list">
        {data.items.length === 0 && (
          <div className="rounded-xl border border-neutral-200 bg-white p-12 text-center text-neutral-500">
            Keine offenen Eingangsrechnungen — alles bezahlt. ✓
          </div>
        )}
        {data.items.map((it, idx) => {
          const style = REC_STYLES[it.recommendation] || REC_STYLES.pay_later;
          return (
            <div key={it.invoice_id} className={`rounded-xl border-2 ${style.border} ${style.bg} p-5`} data-testid={`pp-row-${idx}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={style.badge} variant="secondary">{style.label}</Badge>
                    {it.recommendation === "pay_now_overdue" && <AlertTriangle className="h-4 w-4 text-red-600" />}
                  </div>
                  <div className="font-display text-lg font-semibold text-neutral-900 truncate" data-testid={`pp-supplier-${idx}`}>
                    {it.supplier_name}
                  </div>
                  <div className="text-sm text-neutral-600 mt-0.5">
                    {it.invoice_number || "—"} • Fällig: {fmtDate(it.due_date)}
                    {it.discount_pct ? ` • Skonto: ${it.discount_pct}% bis ${fmtDate(it.discount_deadline)}` : ""}
                  </div>
                  <p className="text-sm text-neutral-700 mt-3 flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{it.reason}</span>
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">Offen</div>
                  <div className="font-display text-2xl font-semibold tabular-nums" data-testid={`pp-amount-${idx}`}>{fmtCurrency(it.outstanding_amount)}</div>
                  {it.skonto_savings > 0 && (
                    <div className="text-xs text-emerald-700 mt-1 font-medium" data-testid={`pp-skonto-${idx}`}>
                      − {fmtCurrency(it.skonto_savings)} bei Skonto
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const color = accent === "red" ? "text-red-700" : accent === "green" ? "text-emerald-700" : "text-neutral-900";
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
